import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, FileCheck, Scale, CheckCircle2 } from 'lucide-react';

const CompliancePage = () => {
  return (
    <div className="dark-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="display-huge page-title">Compliance & Legal</h1>
          <p className="body-large page-subtitle">
            Transparency, compliance, and your consumer rights
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="compliance-intro-section">
        <div className="compliance-intro-content">
          <div className="intro-icon">
            <Shield size={48} />
          </div>
          <h2 className="display-medium intro-title">Our Commitment to Compliance</h2>
          <p className="body-large intro-text">
            The Collins Academy operates in full compliance with all federal and state regulations governing credit services and financial consulting. We believe in transparency, ethical practices, and protecting your consumer rights.
          </p>
        </div>
      </section>

      {/* Regulatory Compliance */}
      <section className="regulatory-section">
        <div className="section-header">
          <h2 className="display-large section-title">Regulatory Framework</h2>
        </div>

        <div className="regulatory-grid">
          <div className="regulatory-card">
            <div className="regulatory-icon">
              <FileCheck size={32} />
            </div>
            <h3 className="heading-2 regulatory-title">Credit Repair Organizations Act (CROA)</h3>
            <p className="body-medium regulatory-text">
              We comply with the Credit Repair Organizations Act (15 U.S.C. §§ 1679-1679j), which regulates credit service organizations. Under CROA, you have specific rights including:
            </p>
            <ul className="regulatory-list">
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>The right to cancel services within 3 business days without charge</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>The right to receive a written contract detailing services and costs</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>Protection from false or misleading representations</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>The right to dispute credit report errors yourself at no cost</span>
              </li>
            </ul>
          </div>

          <div className="regulatory-card">
            <div className="regulatory-icon">
              <Scale size={32} />
            </div>
            <h3 className="heading-2 regulatory-title">Fair Credit Reporting Act (FCRA)</h3>
            <p className="body-medium regulatory-text">
              All credit dispute and optimization strategies comply with the Fair Credit Reporting Act (15 U.S.C. § 1681 et seq.), which governs:
            </p>
            <ul className="regulatory-list">
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>Consumer rights to accurate credit reporting</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>Procedures for disputing inaccurate information</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>Credit bureau obligations to investigate disputes</span>
              </li>
              <li className="regulatory-item">
                <CheckCircle2 size={16} />
                <span>Consumer privacy protections</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Consumer Rights */}
      <section className="consumer-rights-section">
        <div className="section-header">
          <h2 className="display-large section-title">Your Consumer Rights</h2>
          <p className="body-medium section-description">
            Understanding your rights is the foundation of our relationship
          </p>
        </div>

        <div className="rights-grid">
          <div className="rights-card">
            <h3 className="heading-3 rights-title">Right to Cancel</h3>
            <p className="body-medium rights-text">
              You may cancel any contract with us within three (3) business days from the date you signed the agreement. Cancellation must be in writing and sent to our office address. You will receive a full refund of any fees paid if cancelled within this period.
            </p>
          </div>

          <div className="rights-card">
            <h3 className="heading-3 rights-title">Right to Information</h3>
            <p className="body-medium rights-text">
              You have the right to obtain your credit report from each of the three major credit bureaus once every 12 months at no cost through AnnualCreditReport.com. You also have the right to dispute inaccurate information directly with credit bureaus at no cost.
            </p>
          </div>

          <div className="rights-card">
            <h3 className="heading-3 rights-title">Truthful Representations</h3>
            <p className="body-medium rights-text">
              We do not make false claims about results, guaranteed credit score increases, or specific funding amounts. All statements about services are truthful, and results vary based on individual circumstances.
            </p>
          </div>
        </div>
      </section>

      {/* Important Disclosures */}
      <section className="disclosures-section">
        <div className="section-header">
          <h2 className="display-large section-title">Important Disclosures</h2>
        </div>

        <div className="disclosures-container">
          <div className="disclosure-card">
            <h3 className="heading-3 disclosure-title">Results Vary</h3>
            <p className="body-medium disclosure-text">
              Individual results depend on your specific credit history, financial situation, adherence to the program, and actions taken by credit bureaus and lenders. We cannot guarantee specific credit score increases or funding amounts.
            </p>
          </div>

          <div className="disclosure-card">
            <h3 className="heading-3 disclosure-title">No Attorney-Client Relationship</h3>
            <p className="body-medium disclosure-text">
              The Collins Academy is not a law firm and does not provide legal advice. Services are consulting and strategic guidance. For legal matters, we recommend consulting with a licensed attorney.
            </p>
          </div>

          <div className="disclosure-card">
            <h3 className="heading-3 disclosure-title">Business Formation Services</h3>
            <p className="body-medium disclosure-text">
              Business formation services include document preparation and filing assistance. We are not attorneys and do not provide legal advice regarding entity selection, tax implications, or liability protection. Consult with legal and tax professionals for specific advice.
            </p>
          </div>

          <div className="disclosure-card">
            <h3 className="heading-3 disclosure-title">Funding Assistance</h3>
            <p className="body-medium disclosure-text">
              We provide guidance and structure to help position you for funding eligibility. Final funding decisions are made by lending institutions based on their underwriting criteria. We do not guarantee loan approval or specific funding amounts.
            </p>
          </div>

          <div className="disclosure-card">
            <h3 className="heading-3 disclosure-title">Tradeline Services</h3>
            <p className="body-medium disclosure-text">
              Our tradeline services include both primary tradelines (where you are the account holder) and authorized user tradelines (where you are added to existing accounts). We offer a strategic mixture of revolving, installment, and auto accounts to build a well-rounded credit profile. All of our tradelines are guaranteed to post to the credit bureaus within 2 billing cycles or your money back. Tradeline placement is done in compliance with all applicable laws and creditor terms.
            </p>
          </div>
        </div>
      </section>

      {/* State Registrations */}
      <section className="registrations-section">
        <div className="section-header">
          <h2 className="display-large section-title">Licensing & Registration</h2>
        </div>

        <div className="registration-content">
          <p className="body-large registration-text">
            The Collins Academy maintains all required state registrations and surety bonds as mandated by state law for credit services organizations. We are registered in states requiring such registration and maintain appropriate bonding.
          </p>
          <p className="body-medium registration-text">
            For specific registration and bond information for your state, please contact our compliance department.
          </p>
        </div>
      </section>

      {/* Contact Compliance */}
      <section className="compliance-contact-section">
        <div className="compliance-contact-content">
          <h2 className="display-medium compliance-contact-title">Questions About Compliance?</h2>
          <p className="body-large compliance-contact-text">
            Our compliance team is available to answer questions about our practices, your rights, or regulatory matters.
          </p>
          <div className="compliance-contact-info">
            <p className="body-medium">
              <strong>Compliance Department:</strong> information@thecollinsacademy.com
            </p>
            <p className="body-medium">
              <strong>Phone:</strong> 800-201-8582
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="display-large cta-title">Ready to Get Started?</h2>
          <p className="body-large cta-description">
            Work with a compliant, ethical, and transparent financial services partner.
          </p>
          <a 
            href="https://calendly.com/thecollinsacademynew/30min" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="btn-primary cta-button"
          >
            Schedule Your Consultation
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default CompliancePage;
